// Stacking maths for ctx.charts (stacked area, stacked bar). Pure; no DOM.
// KEEP IN SYNC with ./charts-stack.d.mts.
//
// Two things here are load-bearing beyond "add the numbers up":
//   * a null is a GAP, not a zero. A stacked band over a missing value must not
//     claim the series was at zero — it contributes nothing and is marked so the
//     renderer can leave a hole.
//   * mixed signs stack in two directions from the baseline. Summing them into
//     one running total draws a band that crosses its own neighbours.

/** @typedef {{ y0:number, y1:number, value:number|null, gap:boolean }} Band */

/**
 * Stack a set of equal-length series into [y0,y1] bands per point.
 *
 * @param {ReadonlyArray<ReadonlyArray<number|null|undefined>>} series
 * @param {{ offset?: "zero"|"expand" }} [opts]
 *   - `zero`   baseline at 0, positives stack up and negatives stack down
 *   - `expand` normalise each column to 1 (100% stacked); requires same-sign data
 * @returns {Band[][]} same shape as `series`
 */
export function stackSeries(series, opts) {
  const o = opts || {};
  const offset = o.offset === "expand" ? "expand" : "zero";
  const list = series || [];
  const rows = list.length;
  let cols = 0;
  for (const s of list) cols = Math.max(cols, s ? s.length : 0);

  /** @type {Band[][]} */
  const out = list.map(() => new Array(cols));

  for (let c = 0; c < cols; c++) {
    let posTotal = 0;
    let negTotal = 0;
    if (offset === "expand") {
      let sum = 0;
      for (let r = 0; r < rows; r++) {
        const v = num(list[r] && list[r][c]);
        if (v !== null) sum += Math.abs(v);
      }
      for (let r = 0; r < rows; r++) {
        const v = num(list[r] && list[r][c]);
        if (v === null) {
          out[r][c] = { y0: posTotal, y1: posTotal, value: null, gap: true };
          continue;
        }
        const share = sum === 0 ? 0 : Math.abs(v) / sum;
        out[r][c] = { y0: posTotal, y1: posTotal + share, value: v, gap: false };
        posTotal += share;
      }
      continue;
    }

    for (let r = 0; r < rows; r++) {
      const v = num(list[r] && list[r][c]);
      if (v === null) {
        out[r][c] = { y0: posTotal, y1: posTotal, value: null, gap: true };
        continue;
      }
      if (v >= 0) {
        out[r][c] = { y0: posTotal, y1: posTotal + v, value: v, gap: false };
        posTotal += v;
      } else {
        out[r][c] = { y0: negTotal + v, y1: negTotal, value: v, gap: false };
        negTotal += v;
      }
    }
  }
  return out;
}

/** Value extent covered by stacked bands — the domain a stacked chart needs. */
export function stackExtent(stacked) {
  let min = Infinity;
  let max = -Infinity;
  for (const row of stacked || []) {
    for (const b of row || []) {
      if (!b || b.gap) continue;
      if (b.y0 < min) min = b.y0;
      if (b.y1 < min) min = b.y1;
      if (b.y0 > max) max = b.y0;
      if (b.y1 > max) max = b.y1;
    }
  }
  if (min === Infinity) return null;
  // A stack is always read against its baseline; keep zero in frame.
  return { min: Math.min(0, min), max: Math.max(0, max) };
}

/** Column totals (positive + negative summed), skipping gaps. `null` if a column is entirely missing. */
export function columnTotals(series) {
  const list = series || [];
  let cols = 0;
  for (const s of list) cols = Math.max(cols, s ? s.length : 0);
  const out = new Array(cols).fill(null);
  for (let c = 0; c < cols; c++) {
    let sum = 0;
    let any = false;
    for (let r = 0; r < list.length; r++) {
      const v = num(list[r] && list[r][c]);
      if (v === null) continue;
      sum += v;
      any = true;
    }
    out[c] = any ? sum : null;
  }
  return out;
}

/**
 * Shrink a stacked band by the mark spec's 2px surface gap.
 *
 * Adjacent fills must be separated by surface, not by a darker version of
 * themselves — without it a stack of similar hues reads as one blob. The gap is
 * only taken where there IS a neighbour, and never at the cost of erasing a
 * band: segments thinner than the gap keep a 1px sliver so small values stay
 * visible.
 *
 * @param {number} pxA one pixel edge of the segment
 * @param {number} pxB the other edge
 * @param {{ gap?: number, before?: boolean, after?: boolean, min?: number }} [opts]
 * @returns {{ a:number, b:number }} ordered so a <= b
 */
export function applySurfaceGap(pxA, pxB, opts) {
  const o = opts || {};
  const gap = Number.isFinite(o.gap) ? o.gap : 2;
  const minKeep = Number.isFinite(o.min) ? o.min : 1;
  let a = Math.min(pxA, pxB);
  let b = Math.max(pxA, pxB);
  const half = gap / 2;
  const wantA = o.before !== false ? half : 0;
  const wantB = o.after !== false ? half : 0;
  const size = b - a;
  if (size - (wantA + wantB) < minKeep) {
    // Too thin to give the gap away — keep the mark, drop the gap.
    const keep = Math.max(minKeep, size);
    const mid = (a + b) / 2;
    return { a: mid - keep / 2, b: mid + keep / 2 };
  }
  a += wantA;
  b -= wantB;
  return { a, b };
}

/** @param {unknown} v @returns {number|null} */
function num(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
